import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Alert,
  Button,
  Col,
  FullWidthLayoutCardContent,
  LayoutCard,
  Link,
  SkeletonLoader,
  Text,
  Title,
  useComponentTheme,
  withSpacingStyles,
} from '@vmos2/design-system';
import { useConfig, usePageLoading, useSettings, useUser } from '@vmos2/providers';
import { useLocale } from '@vmos2/utils';
import orderBy from 'lodash/orderBy';
import type { ChangeEvent, ComponentType } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DiscountErrorReasons,
  UserPromotion as UserPromotionType,
  UserPromotions as UserPromotionsType,
} from '../../flow-types';
import { LoyaltyLogo } from '../..';
import { pages, RewardsExternalLink, UserPromotion, UserPromotions } from '..';

const getBadgeInformation = (creationDateString, expiryDateString, showNewAfterDays, showExpiryBeforeDays) => {
  let daysUntilExpiry = 0;
  let showNewBadge = false;
  let showExpiryBadge = false;

  if (creationDateString || expiryDateString) {
    // Use `setHours(0, 0, 0, 0)` for all the dates to give them a consistent time, because we only care about the date,
    // and not the time.
    const currentDateMidnight = new Date().setHours(0, 0, 0, 0);

    if (creationDateString) {
      const createdDate = new Date(creationDateString).setHours(0, 0, 0, 0);
      const showNewUntilDate = new Date(createdDate);

      showNewUntilDate.setDate(showNewUntilDate.getDate() + showNewAfterDays);

      showNewBadge = currentDateMidnight <= showNewUntilDate;
    }

    if (expiryDateString) {
      const expiryDate = new Date(expiryDateString).setHours(0, 0, 0, 0);
      const showExpiryFromDate = new Date(expiryDate);

      showExpiryFromDate.setDate(showExpiryFromDate.getDate() - showExpiryBeforeDays);

      // 86400000 = 24 hours
      // Use Math.round to account for time zone shifts.
      daysUntilExpiry = Math.round((expiryDate - currentDateMidnight) / 86400000);
      showExpiryBadge = currentDateMidnight >= showExpiryFromDate;
    }
  }

  return { daysUntilExpiry, showExpiryBadge, showNewBadge };
};

const getDates = userPromotion => ({
  creationDateString: userPromotion.userReward ? userPromotion.userReward.createdAt : userPromotion.createdAt,
  expiryDateString: userPromotion.userReward ? userPromotion.userReward.usableUntil : userPromotion.endsAt,
});

const rewardWidths = {
  'full-width': '100%',
  large: '280px',
  small: '120px',
};

const sortByDate = (userPromotions, dateName, showInDescendingOrder) =>
  // Use `slice` so we don't mutate the original list.
  userPromotions.slice().sort((userPromotionA, userPromotionB) => {
    const dateStringA = getDates(userPromotionA)[dateName];
    const dateStringB = getDates(userPromotionB)[dateName];

    if (!dateStringA) {
      return 1;
    }

    if (!dateStringB) {
      return -1;
    }

    if (showInDescendingOrder) {
      return dateStringA < dateStringB ? 1 : -1;
    }

    return dateStringA > dateStringB ? 1 : -1;
  });

type Props = {
  brand?: { slug: string, uuid: string },
  className?: string,
  componentInformation?: {
    direction: 'vertical' | 'horizontal',
    name: string,
    style: 'large' | 'small',
    type: 'rewardsList',
  },
  currentApplyingPromotion?: { uuid: string },
  disabled?: boolean,
  discountedAmount?: number,
  errorReasons?: { [key: string]: DiscountErrorReasons } | DiscountErrorReasons,
  fetchMenus?: () => void,
  hasLoyaltyPointsDiscount?: boolean,
  hasMultipleSelect?: boolean,
  isApplyingDiscount?: boolean,
  isLoading?: boolean,
  mode?: string,
  onChange?: (e: ChangeEvent, userPromotion: UserPromotionType) => void,
  promotionsBeingApplied?: string[],
  selectedPromotions: UserPromotionsType,
  showButton?: boolean,
  showDetailsLink?: boolean,
  showNewFirst?: boolean,
  showQrCode?: boolean,
  showTitle?: boolean,
  storeUUID?: string,
  titleLevel?: 1 | 2 | 3 | 4 | 5,
  titleMode?: string,
  totalBundlesAmount?: number,
  translationContext?: string,
  userPromotions: UserPromotionsType,
  wrapperElement?: ComponentType,
};

const DEFAULT_COMPONENT_INFORMATION = {};

export const SimpleRewardsList = withSpacingStyles(
  ({
    brand,
    className,
    componentInformation = DEFAULT_COMPONENT_INFORMATION,
    currentApplyingPromotion,
    disabled,
    discountedAmount,
    errorReasons,
    fetchMenus,
    hasLoyaltyPointsDiscount,
    hasMultipleSelect,
    isApplyingDiscount,
    isLoading = false,
    mode,
    onChange,
    promotionsBeingApplied,
    selectedPromotions,
    showButton = true,
    showDetailsLink = true,
    showNewFirst = true,
    showQrCode = true,
    showTitle = true,
    storeUUID,
    titleLevel = 2,
    titleMode,
    totalBundlesAmount,
    translationContext,
    userPromotions,
    wrapperElement: Wrapper = LayoutCard,
  }: Props) => {
    const {
      claimButtonVisible,
      redeemRewardNote: showInfo,
      showEmptyState,
      showExpiryBeforeDays,
      showNewAfterDays,
      showNewAndExpiryBadges,
    } = useComponentTheme(theme => theme.components.SimpleRewardsList);
    const config = useConfig();
    const settings = useSettings();
    const { locale } = useLocale(config, settings);
    const { isLoading: isLoadingPage, setComponentLoaded } = usePageLoading();
    const { deliverooMenu } = settings;
    const { t } = useTranslation('profile.rewards');
    const { isLoggedIn } = useUser();
    const { direction, name: componentName, style, theme: componentTheme = {} } = componentInformation;
    const sectionTheme = componentTheme?.section || {};

    const sortedUserPromotions = useMemo(() => {
      if (userPromotions?.length) {
        if (showNewAndExpiryBadges) {
          const { disabledPromotions, newPromotions, otherPromotions } = userPromotions.reduce(
            (categories, userPromotion) => {
              const { creationDateString, expiryDateString } = getDates(userPromotion);

              const badgeInformation = getBadgeInformation(
                creationDateString,
                expiryDateString,
                showNewAfterDays,
                showExpiryBeforeDays
              );

              const userPromotionWithBadgeInformation = { ...userPromotion, badgeInformation };

              if (onChange && !userPromotion.isActive) {
                categories.disabledPromotions.push(userPromotionWithBadgeInformation);
              } else if (showNewFirst && badgeInformation.showNewBadge) {
                categories.newPromotions.push(userPromotionWithBadgeInformation);
              } else {
                categories.otherPromotions.push(userPromotionWithBadgeInformation);
              }

              return categories;
            },
            { disabledPromotions: [], newPromotions: [], otherPromotions: [] }
          );

          return [
            ...sortByDate(newPromotions, 'creationDateString', true),
            ...sortByDate(otherPromotions, 'expiryDateString'),
            ...sortByDate(disabledPromotions, 'expiryDateString'),
          ];
        }

        return orderBy(userPromotions, 'isActive', 'desc');
      }

      return null;
    }, [onChange, showExpiryBeforeDays, showNewAfterDays, showNewAndExpiryBadges, showNewFirst, userPromotions]);

    useEffect(() => {
      if (!isLoading) {
        setComponentLoaded?.(componentName);
      }
    }, [componentName, isLoading, setComponentLoaded]);

    useEffect(() => {
      const shouldGetMenus = showDetailsLink && storeUUID && !brand?.uuid && !!fetchMenus;

      if (shouldGetMenus) {
        fetchMenus({ deliverooMenu }, { locale, store: storeUUID });
      }
    }, [brand?.uuid, deliverooMenu, fetchMenus, locale, showDetailsLink, storeUUID]);

    if (!isLoadingPage && (!isLoggedIn || (!sortedUserPromotions?.length && !showEmptyState))) {
      return null;
    }

    const totalUserPromotionsCount =
      sortedUserPromotions?.reduce((totalCount, { count = 1 }) => totalCount + count, 0) ?? 0;

    const availableDiscounts = t('rewardsList.availableDiscounts', { count: totalUserPromotionsCount });
    const title = t('rewardsList.title');

    return (
      <Wrapper
        backgroundColor={sectionTheme.backgroundColor}
        backgroundCover={sectionTheme.backgroundCover}
        borderRadius={sectionTheme.backgroundCover ? 0 : undefined}
        className={className}
        data-test="promotions-list-wrapper"
        isLoading={isLoadingPage}
        margin={sectionTheme.backgroundCover ? 0 : undefined}
        mode={mode}
        {...(showTitle && {
          title: (
            <Title level={titleLevel} margin={titleLevel === 1 ? 'small' : 'normal'} mode={titleMode}>
              {title}
            </Title>
          ),
        })}
      >
        <SkeletonLoader isLoading={isLoadingPage}>
          {!sortedUserPromotions?.length && showEmptyState ? (
            <UserPromotion
              userPromotion={{
                description: t('rewardsList.noRewardsDescription'),
                image: <LoyaltyLogo mode="SimpleRewardsList" size="100%" />,
                name: t('rewardsList.noRewardsName'),
              }}
            />
          ) : (
            <Content data-test="promotions-list-content" {...(onChange && { 'aria-label': title, as: 'fieldset' })}>
              <Intro data-test="promotions-list-content-intro">
                {availableDiscounts && (
                  <Text level={3} strong>
                    {availableDiscounts}
                  </Text>
                )}

                {showInfo && (
                  <Alert type="info">
                    <Text level={3}>
                      {t('rewardsList.toRedeem', { context: translationContext, count: totalUserPromotionsCount })}
                    </Text>
                  </Alert>
                )}

                {showDetailsLink && (
                  <RewardsExternalLink brandSlug={brand?.slug} page={pages.rewards}>
                    {t('rewardsList.moreDetails')}
                  </RewardsExternalLink>
                )}
              </Intro>

              <FullWidthLayoutCardContent
                css={theme => css`
                  display: grid;
                  grid-gap: ${theme.margins.md};
                  list-style: none;

                  ${direction === 'horizontal' && sortedUserPromotions?.length > 1
                    ? `
                      grid-auto-columns: ${rewardWidths[style]};
                      grid-auto-flow: column;
                    `
                    : ''}
                `}
                data-test="promotions-list-content-promotions"
                hasHorizontallScrolling
                {...(!onChange && { as: 'ul' })}
              >
                <UserPromotions
                  currentApplyingPromotion={currentApplyingPromotion}
                  disabled={disabled}
                  discountedAmount={discountedAmount}
                  errorReasons={errorReasons}
                  hasLoyaltyPointsDiscount={hasLoyaltyPointsDiscount}
                  hasMultipleSelect={hasMultipleSelect}
                  isApplyingDiscount={isApplyingDiscount}
                  onChange={onChange}
                  promotionsBeingApplied={promotionsBeingApplied}
                  selectedPromotions={selectedPromotions}
                  showDescription={direction !== 'horizontal'}
                  showQrCode={showQrCode}
                  totalBundlesAmount={totalBundlesAmount}
                  userPromotions={sortedUserPromotions}
                />
              </FullWidthLayoutCardContent>

              {showButton && claimButtonVisible && (
                <Col md={6} mdOffset={3} mt={6} noPadding>
                  <Button as={Link} block contentCenter to="store-selection" size="small" type="button">
                    {t('rewardsList.claimReward')}
                  </Button>
                </Col>
              )}
            </Content>
          )}
        </SkeletonLoader>
      </Wrapper>
    );
  }
);

SimpleRewardsList.displayName = 'SimpleRewardsList';

const Content = styled.div`
  width: 100%;
  padding: 0;
  border: none;
  margin: 0;
`;

const Intro = styled.div(
  ({ theme }) => `
    :not(:empty) {
      display: grid;
      grid-row-gap: ${theme.margins.xs};
      width: 100%;
      margin-bottom: ${theme.margins.md};
    }
  `
);
